type BgblPart = 'I' | 'II' | 'III'

type ParsedCitation = {
  year: number
  docNumber: string
  part?: BgblPart
}

type Pre2004Links = {
  kind: 'pre2004'
  pdf: string
}

type Post2004Links = {
  kind: 'post2004'
  html: string
  pdf: string
  pdfsig: string
}

export const parseBgbl = (input: string | null | undefined): ParsedCitation | null => {
  if (!input) return null
  // Matches:
  //  - "BGBl I 61/2007"
  //  - "I 61/2007"
  //  - "BGBl. I Nr. 138/2003"
  //  - "BGBl 394/1986" (no part, older format)
  const m = input.match(/(?:BGBl\.?\s*)?(?:(I|II|III)\s*)?(?:Nr\.?\s*)?(\d+)\s*[/-]\s*(\d{4})/i)
  if (!m) return null

  const part = m[1] ? (m[1].toUpperCase() as BgblPart) : undefined
  const docNumber = m[2]
  const year = Number(m[3])

  return {part, docNumber, year}
}

/*
 * Build RIS document links without needing publication dates:
 *  - For year ≤ 2003: BgblPdf (PDF only)
 *  - For year ≥ 2004: BgblAuth (HTML, PDF, PDFSIG) — requires part (I|II|III)
 *
 * Examples:
 *  - "BGBl 394/1986"          → pre2004 PDF
 *  - "BGBl II 142/2012"       → post2004 HTML/PDF/PDFSIG
 *  - "BGBl. I Nr. 138/2003"   → pre2004 PDF
 */

const getBatchPart = (year: number) => {
  if (year >= 2002) {
    return '_1'
  }
  if (year >= 1997 && year <= 2001) {
    return '_2'
  }
  return '_0'
}

/*
 * Test with Kommentar Arbeitsverfassungsrecht $ 97 ARBVG
 */
export const buildBgblLink = (input: string | null | undefined): Pre2004Links | Post2004Links | null => {
  const parsed = parseBgbl(input)
  if (!parsed) {
    return null
  }

  const {year, docNumber, part} = parsed

  if (year <= 2003) {
    // Pre-2004: no part, stable BgblPdf path
    // TODO However, the '_0' is not stable and can't be predicted:
    // We are hitting a legacy quirk: pre-2004 BGBl PDFs use an internal “Dokumentnummer” like 2003_138_1. The last digit
    // isn’t derivable from the citation alone. A clean way—no guessing—is to resolve the Dokumentnummer via the RIS search
    // page and then build the PDF URL.
    // When we generate the pages on the server, we could fix this.
    const batchPart = getBatchPart(year)
    const baseLink = `https://www.ris.bka.gv.at/Dokumente/BgblPdf/${year}_${docNumber}${batchPart}/${year}_${docNumber}${batchPart}`
    return {kind: 'pre2004', pdf: `${baseLink}.pdf`}
  }

  // 2004 and later require the BGBl part
  if (!part) {
    return null
  }

  const base = `https://www.ris.bka.gv.at/Dokumente/BgblAuth/BGBLA_${year}_${part}_${docNumber}/BGBLA_${year}_${part}_${docNumber}`
  return {
    kind: 'post2004',
    html: `${base}.html`,
    pdf: `${base}.pdf`,
    pdfsig: `${base}.pdfsig`,
  }
}
